import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import {
    Outlet,
    useLocation,
    useNavigate,
    useOutletContext,
} from 'react-router';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { useEnforcementLogFilterConfiguration } from '../shared-mui-components/filter/OperatorFilterConfiguration.tsx';
import { RequestStatus } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    FilterConfig,
    makeSearchQueryWithFilters,
} from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import {
    CancelOutlined,
    CheckCircleOutlined,
    Checklist,
    CloudDownload,
    HelpOutlineOutlined,
} from '@mui/icons-material';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import { FiltersBox } from '../shared-mui-components/filter/FiltersBox.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import {
    EmptyResults,
    FetchError,
    ResultLimitAlert,
} from '../shared-mui-components/ListMessages.tsx';
import { JSX, useMemo } from 'react';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { ButtonWithDropdown } from '../layout/components/ButtonWithDropdown.tsx';

export interface EnforcementLogSearchResult {
    results: EnforcementLogItem[];
    eof: boolean;
}

export interface EnforcementLogItem {
    enforcementLogId: number;
    identification: string;
    enforcementTime: string;
    zoneName: string;
    locationName: string;
    responseResult: ResponseResult;
    enforcerName: string;
    rawIdentification: string;
    recognitionType: string;
    clientName: string;
    location: string | null;
    locationState: string | null;
}

export const enum ResponseResult {
    POSITIVE = 'POSITIVE',
    FUZZY_POSITIVE = 'FUZZY_POSITIVE',
    NEGATIVE = 'NEGATIVE',
}

export function useEnforcementLogOutlet() {
    return useOutletContext<EnforcementLogOutletContext>();
}

export function EnforcementLogOutlet({
    context,
}: {
    context: EnforcementLogOutletContext;
}) {
    return <Outlet context={context} />;
}

export interface EnforcementLogOutletContext {
    enforcementLogList: EnforcementLogItem[];
    searchText: string | null;
    activeFilters: FilterConfig;
}

export function EnforcementLogListComp() {
    const {
        operatorExternalId,
        operatorData: { hasNightParking },
    } = useOperatorContext();
    const navigate = useNavigate();
    const { language } = useLanguage();

    const {
        filterConfig,
        activeFilters,
        listState,
        refetchList,
        searchText,
        filterConfigState,
    } = useEnforcementLogFilterConfiguration();
    const { search } = useLocation();
    const paramsWithLanguage = useMemo(() => {
        const params = new URLSearchParams(search);
        params.append('language', language);
        return params;
    }, [search, language]);

    const nightParkingExport = hasNightParking
        ? [
              {
                  key: 'night-parking',
                  label: (
                      <Localized
                          de="Statistik Nachtparkieren"
                          fr="Statistique stationnement nocturne"
                          it="Statistica parcheggio notturno "
                          en="Night parking statistic"
                      />
                  ),
                  onClickCallback: () => {
                      openNewTab(
                          urlWithOperatorExternalId(
                              `/ui-api/operator-account/enforcement-log/night-parking-export?${paramsWithLanguage.toString()}`,
                              operatorExternalId,
                          ),
                      );
                  },
                  disabled: false,
              },
          ]
        : [];

    return (
        <MainContent>
            <HeaderWithButtons
                title={
                    <Localized
                        de="Kontroll-History"
                        fr="Historique des contrôles"
                        it="Storico controlli"
                        en="Enforcement History"
                    />
                }
                icon={Checklist}
                headerButtons={
                    listState.status === RequestStatus.SUCCESS &&
                    listState.data.results.length > 0 ? (
                        <ButtonWithDropdown
                            variant="outlined"
                            label={
                                <Localized
                                    de="Exportieren"
                                    fr="Exporter"
                                    it="Esporta"
                                    en="Export"
                                />
                            }
                            startIcon={<CloudDownload />}
                            dropdownItems={[
                                {
                                    key: 'download-list',
                                    label: (
                                        <Localized
                                            de="Exportieren"
                                            fr="Exporter"
                                            it="Esporta"
                                            en="Export"
                                        />
                                    ),
                                    onClickCallback: () => {
                                        openNewTab(
                                            urlWithOperatorExternalId(
                                                `/ui-api/operator-account/enforcement-log/export?${paramsWithLanguage.toString()}`,
                                                operatorExternalId,
                                            ),
                                        );
                                    },
                                    disabled: false,
                                },
                                {
                                    key: 'login-csv',
                                    label: (
                                        <Localized
                                            de="Statistik pro Kontrolleur"
                                            fr="Statistique par contrôleur"
                                            it="Statistica per controllore"
                                            en="Control zones statistics"
                                        />
                                    ),
                                    onClickCallback: () => {
                                        openNewTab(
                                            urlWithOperatorExternalId(
                                                `/ui-api/operator-account/enforcement-log/login-csv-export?${paramsWithLanguage.toString()}`,
                                                operatorExternalId,
                                            ),
                                        );
                                    },
                                    disabled: false,
                                },
                                {
                                    key: 'zone-csv',
                                    label: (
                                        <Localized
                                            de="Statistik pro Kontrollzone"
                                            fr="Statistique par zone de contrôle"
                                            it="Statistica per zona di controllo"
                                            en="Control zones statistics"
                                        />
                                    ),
                                    onClickCallback: () => {
                                        openNewTab(
                                            urlWithOperatorExternalId(
                                                `/ui-api/operator-account/enforcement-log/zone-csv-export?${paramsWithLanguage.toString()}`,
                                                operatorExternalId,
                                            ),
                                        );
                                    },
                                    disabled: false,
                                },
                                ...nightParkingExport,
                            ]}
                        />
                    ) : undefined
                }
            />
            <FiltersBox
                searchText={searchText}
                activeFilters={activeFilters}
                filterConfig={filterConfig}
                requestState={listState}
                refetch={refetchList}
                pending={filterConfigState.status === RequestStatus.PENDING}
                tooltip={''}
            />
            <OperatorAsyncLoadedSection
                requestState={listState}
                renderError={() => <FetchError refetchList={refetchList} />}
                render={data => {
                    return (
                        <>
                            <EnforcementLogList
                                data={data}
                                onRowClick={(id: number) => {
                                    navigate(
                                        `${id}/detail${makeSearchQueryWithFilters(
                                            searchText,
                                            activeFilters,
                                        )}`,
                                    );
                                }}
                            />
                            <EnforcementLogOutlet
                                context={{
                                    enforcementLogList: data.results,
                                    searchText,
                                    activeFilters,
                                }}
                            />
                        </>
                    );
                }}
            />
        </MainContent>
    );
}

function EnforcementLogList({
    data,
    onRowClick,
}: {
    data: EnforcementLogSearchResult;
    onRowClick: (id: number) => void;
}) {
    if (data.results.length < 1) {
        return <EmptyResults />;
    }

    return (
        <TableContainer>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Localized
                                de="Kontrollzeit"
                                fr="Heure du contrôle"
                                it="Ora del controllo"
                                en="Control time"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Kennzeichen"
                                fr="Immatriculation"
                                it="Targa"
                                en="License plate"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Zone"
                                fr="Zone"
                                it="Zona"
                                en="Zone"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Ort"
                                fr="Localité"
                                it="Località"
                                en="Place"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Kontrolleur"
                                fr="Contrôleur"
                                it="Controllore"
                                en="Controller"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Resultat"
                                fr="Résultat"
                                it="Risultato"
                                en="Resultat"
                            />
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.results.map(item => (
                        <EnforcementLogRow
                            key={item.enforcementLogId}
                            item={item}
                            onRowClick={onRowClick}
                        />
                    ))}
                </TableBody>
            </Table>
            <ResultLimitAlert
                eof={data.eof}
                numberOfResults={data.results.length}
                hasExport={true}
            />
        </TableContainer>
    );
}

function EnforcementLogRow({
    item,
    onRowClick,
}: {
    item: EnforcementLogItem;
    onRowClick: (id: number) => void;
}) {
    return (
        <TableRow
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() => onRowClick(item.enforcementLogId)}
        >
            <TableCell>
                {Formatter.dayMonthYearHourMinuteSecond(item.enforcementTime)}
            </TableCell>
            <TableCell>{item.identification}</TableCell>
            <TableCell>{item.zoneName}</TableCell>
            <TableCell>{item.locationName}</TableCell>
            <TableCell>{item.enforcerName}</TableCell>
            <TableCell>
                <EnforcementLogResponseIcon result={item.responseResult} />
            </TableCell>
        </TableRow>
    );
}

export function EnforcementLogResponseIcon({
    result,
}: {
    result: ResponseResult;
}) {
    switch (result) {
        case ResponseResult.POSITIVE:
            return (
                <Tooltip
                    title={<EnforcementLogResponse result={result} />}
                    placement="right"
                >
                    <CheckCircleOutlined color="success" />
                </Tooltip>
            );
        case ResponseResult.FUZZY_POSITIVE:
            return (
                <Tooltip
                    title={<EnforcementLogResponse result={result} />}
                    placement="right"
                >
                    <HelpOutlineOutlined sx={{ color: 'warning.main' }} />
                </Tooltip>
            );
        case ResponseResult.NEGATIVE:
            return (
                <Tooltip
                    title={<EnforcementLogResponse result={result} />}
                    placement="right"
                >
                    <CancelOutlined sx={{ color: 'error.main' }} />
                </Tooltip>
            );
    }
}

export function EnforcementLogResponse({
    result,
}: {
    result: ResponseResult;
}): JSX.Element {
    switch (result) {
        case ResponseResult.POSITIVE:
            return (
                <Localized
                    de="positiv"
                    fr="positif"
                    it="positivo"
                    en="positive"
                />
            );
        case ResponseResult.FUZZY_POSITIVE:
            return (
                <Localized
                    de="vielleicht positiv"
                    fr="peut-être positif"
                    it="forse positivo"
                    en="maybe positive"
                />
            );
        case ResponseResult.NEGATIVE:
            return (
                <Localized
                    de="negativ"
                    fr="négatif"
                    it="negativo"
                    en="negative"
                />
            );
    }
}
